import { useDispatch } from "react-redux";

import { Map } from "maplibre-gl";
import { FeatureCollection, Geometry } from "geojson";

import { DEFAULT_STEP } from "@/constants/dataset";
import { SUBLAYERS } from "@/constants/map";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { updateDatasetStyle } from "@/store/slice/datasetSlice";
import { Dataset, DatasetAttributeSchema, DatasetStyle } from "@/types/dataset";
import { isOrdinalType } from "@/utils/dataset";
import { addLayer } from "@/utils/map";
import {
  addLineStringOrdinalLayers,
  addLineStringQuantileLayers,
  addLineStringSource
} from "@/utils/map/line";

interface UseLineLayersProps {
  map: Map;
  prepareLayerStyles: (
    parsedFeatureCollectionData: FeatureCollection<Geometry>,
    dataset: Dataset,
    selectedCategory: DatasetAttributeSchema,
    step: number
  ) => { updatedStyle: DatasetStyle };
  handleFeatureClick: (layerId: string) => void;
  handleMovePositionToLayer: (polygonData: FeatureCollection<Geometry>) => void;
  handleUpdateSourceWithRank: (value: {
    datasetId: string;
    style: DatasetStyle;
    update: {
      isEnabled?: boolean;
      maxRanking?: number;
      category?: DatasetAttributeSchema;
    };
  }) => Promise<void>;
}

export const useLineLayers = ({
  map,
  prepareLayerStyles,
  handleFeatureClick,
  handleMovePositionToLayer,
  handleUpdateSourceWithRank
}: UseLineLayersProps) => {
  const { isNewUIDatasetConfigEnabled, isRankingLabelEnabled } =
    useAppFeatures();
  const dispatch = useDispatch();

  const handleAddLineLayer = (
    dataset: Dataset,
    lineData: FeatureCollection<Geometry>,
    customId?: string
  ) => {
    const sourceId = customId || dataset.data.id;
    if (!map) return;
    // Check if the dataset source already exists on the map
    if (map.getSource(sourceId)) return;

    const newLayer = {
      id: sourceId,
      type: dataset.properties.spatialType,
      sublayers: { ...SUBLAYERS(dataset.data.id)["line"] }
    };

    // Use stroke attribute for line layers
    const strokeAttributeName =
      dataset.style.stroke?.attribute_name ??
      dataset.selectedCategory.attribute_name;
    const strokeAttribute =
      dataset.categories.find(
        (c) => c.attribute_name === strokeAttributeName
      ) ?? dataset.selectedCategory;

    const { updatedStyle } = prepareLayerStyles(
      lineData,
      dataset,
      strokeAttribute,
      dataset.style.colorSteps || DEFAULT_STEP
    );

    addLineStringSource(map, sourceId, lineData);

    const isOrdinal = isOrdinalType(strokeAttribute.data_type);

    addLayer({
      isNewUIDatasetConfigEnabled,
      isRankingLabelEnabled,
      map,
      layerData: newLayer,
      style: updatedStyle,
      selectedCategory: strokeAttribute.attribute_name,
      isOrdinal,
      addOrdinalFunc: addLineStringOrdinalLayers,
      addQuantileFunc: addLineStringQuantileLayers
    });

    handleUpdateSourceWithRank({
      datasetId: sourceId,
      style: updatedStyle,
      update: {}
    });

    dispatch(
      updateDatasetStyle({ datasetId: dataset.data.id, style: updatedStyle })
    );

    handleFeatureClick(newLayer.sublayers.stroke);

    if (dataset.properties.isVisible) handleMovePositionToLayer(lineData);
  };

  return {
    handleAddLineLayer
  };
};
